// live/LiveSolo.tsx (GH3) - the LIVE Solo surface. Training wheels off on a FRESHLY GENERATED hand: tag the
// player, lock the read, TYPE both price numbers yourself (gated - no acting until they are in), choose the
// play from the TRUE legal set, and get NO inline grade. One end-of-hand report closes the hand, graded on
// the read, the price, and the play. Reuses <ReadLockIn>, <SoloPriceEntry>, <BetMathBuilder> (via
// <LiveActionRamp>), and <EndOfHandReport> verbatim.
//
// Fold behavior (spec, locked 2026-07-13): a fold ENDS the hand → into the report → next hand. No forced
// watching. (Solo shows no inline grade, so the wrong-fold coach card + counterfactual seam surface in the
// report/GH6 rather than inline.)
import { useEffect, useMemo, useRef, useState } from 'react';
import { initialLock, lockReducer } from '@postflop/engine';
import type { ReadLock } from '@postflop/schema';
import { CinematicFelt } from '../components/CinematicFelt';
import { streamFor } from '../cinematic/streamFor';
import { narrateHand } from '../cinematic/narrate';
import { heroDecisionFrame, heroActionFrame } from '../cinematic/lens';
import { StreetStepper } from '../components/StreetStepper';
import { ReadLockIn } from '../diagnose/ReadLockIn';
import { SoloPriceEntry } from '../solo/SoloPriceEntry';
import type { PlayerTag } from '../diagnose/data';
import { EndOfHandReport } from '../report/EndOfHandReport';
import { useDebriefProse } from './narration';
import { persistPlayedHand } from '../progress/persistPlay';
import type { MoveClass, ResolvedGrade } from '../grade/types';
import type { RampAction } from '../engine/liveHand';
import { money } from '../engine/bridge';
import { buildFoldedWalk, canWatchFold } from '../engine/liveRunner';
import { LiveActionRamp } from './LiveActionRamp';
import { LIVE_FAMILIES, buildLiveReport, type LiveReportEntry } from './liveView';
import { stepViewFor } from './stepView';
import { preflopContextLine } from './preflopRecap';
import { buildFoldedObservation, type FoldedObservation } from './foldedObservation';
import { FoldedObservationView } from './FoldedObservationView';
import type { LiveSurfaceProps } from './LiveObserve';
import { dollars } from '../home/stakes';
import type { Roll } from '../bankroll/bankroll';

type Phase = 'play' | 'logged' | 'report' | 'watch';

/**
 * CX6 - the Solo-ONLY bankroll wiring. Observe and Diagnose never receive this bundle, which is precisely how
 * "they never touch the roll" holds by construction (North Star §4.4). `choice` is the sit-down decision:
 *  - `null`  - not chosen yet -> the sit-down gate is shown (sit with your roll, or sit fresh);
 *  - `'roll'` - a concentrated session: the buy-in is on the line and every hand settles into the persisted
 *    roll, which carries across app sessions;
 *  - `'fresh'` - a casual sit: no readout, no settle, the roll is untouched.
 *
 * `roll` is the persisted, SIMULATED play-money roll for the chosen stakes; `onSettleHand` signals "this hand
 * is done" and App reads the authoritative net off its own table and persists it (revisable + idempotent).
 */
export interface SoloBankroll {
  choice: 'roll' | 'fresh' | null;
  onChoose: (choice: 'roll' | 'fresh') => void;
  roll: Roll;
  buyIn: number;
  stakesLabel: string;
  onSettleHand: () => void;
  onResetRoll: () => void;
}

/** Demote a move class one notch (for a missed price) - best→exc→inacc; great→exc. */
const demote = (c: MoveClass): MoveClass => (c === 'best' ? 'exc' : c === 'great' ? 'exc' : c === 'exc' ? 'inacc' : c);

// TB2: the hand is the SESSION's (App holds the table); it already carries hero's real played preflop line.
export function LiveSolo({
  model,
  onNextHand,
  onLockRead,
  seatTags,
  onConfirmReads,
  onHeroDecision,
  bankroll,
}: LiveSurfaceProps & { bankroll: SoloBankroll }) {
  const [i, setI] = useState(0);
  const step = model.steps[i];
  // NB: no `isLast` here on purpose. Whether a step is the last one is only knowable from the model AFTER
  // hero's action is played into it - see `commit` and the hand-ended effect below.
  const sv = useMemo(() => stepViewFor(step, model), [step, model]);

  // CX4 - the ONE renderer (same as Observe/Diagnose), in the SOLO config: the felt plays and halts at hero's
  // decisions exactly like Diagnose, but LIVE narration is SILENT (North Star §5: "narration held to the
  // debrief"). The pro's read of the whole hand is replayed with the SAME narrator (observe lens) in the
  // end-of-hand report - `debriefBeats` below. `feltIndex` arms the ramp only once the action reaches hero.
  const stream = useMemo(() => streamFor(model), [model]);
  const [feltIndex, setFeltIndex] = useState(0);
  // the debrief replay (North Star §5 "narration held to the debrief"): the whole hand watched back with the
  // pro's read, using the SAME cinematic renderer + narrator in the OBSERVE lens. Built only when asked for.
  const [replaying, setReplaying] = useState(false);
  const debriefBeats = useMemo(() => narrateHand(stream, model, { lens: 'observe' }), [stream, model]);
  // CX7: the offline library prose lane for the debrief. OFF by default (VITE_NARRATION_ON unset) ⇒ [] ⇒
  // nothing renders (the debrief is unchanged). ON ⇒ each decision resolves through the default-live /
  // fall-back-baked selection: offline ⇒ baked library ($0), connected+live ⇒ fresh. This is the surface
  // that proves an offline narrated hand plays from the baked library.
  const debriefDigests = useMemo(() => model.snapshots.map((s) => s.digest), [model]);
  const debriefProse = useDebriefProse(debriefDigests);
  // GH9: ground the flop-first entry with a one-line preflop recap. Suppressed once preflop is a real
  // playable step (PF4) - the user just played it.
  const pfctx = useMemo(
    () => (!model.preflop && step.kind === 'postflop' && step.streetIndex === 1 ? preflopContextLine(model) : null),
    [model, step],
  );
  const hasPrice = sv.hasPrice;
  const reqEq = sv.reqEq;

  const [tag, setTag] = useState<PlayerTag | null>(null);
  const [weights, setWeights] = useState<number[]>(LIVE_FAMILIES.map(() => 0));
  const [lock, setLock] = useState<ReadLock>(initialLock);
  const [callTyped, setCallTyped] = useState('');
  const [pctTyped, setPctTyped] = useState('');
  const [phase, setPhase] = useState<Phase>('play');
  const [results, setResults] = useState<(LiveReportEntry | null)[]>(() => model.steps.map(() => null));
  const persistedRef = useRef(false);
  // TB4: the step hero folded on (null = he did not fold), and the narrated aftermath once he asks for it.
  // Solo commits a fold straight into the report, so the step index has to be remembered - by then `i` is
  // still the fold's step, but the report is a different view and nothing else pins it.
  const [foldedStep, setFoldedStep] = useState<number | null>(null);
  const [obs, setObs] = useState<FoldedObservation | null>(null);
  /** the step index hero last COMMITTED at - see the hand-ended effect below for why an index is not enough. */
  const loggedAtRef = useRef<number | null>(null);

  const locked = lock.state === 'lockedIn';
  const priceReady = !hasPrice || (callTyped.trim() !== '' && pctTyped.trim() !== '');

  useEffect(() => {
    setTag(null);
    setWeights(LIVE_FAMILIES.map(() => 0));
    setLock(initialLock());
    setCallTyped('');
    setPctTyped('');
    setPhase('play');
    window.scrollTo({ top: 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i]);

  // Clear the scorecard when a NEW HAND arrives - keyed on `handId`, NOT on the model object.
  //
  // FULL: this used to depend on `[model]`, which was safe only while a hand was dealt exactly once and its
  // model never changed again. Now every decision you play rebuilds the model, so an identity-keyed reset
  // would wipe your own grades the instant you acted. `handId` is stable across a rebuild and changes on the
  // next hand, which is precisely the question this effect is asking. Deliberately NOT keyed on
  // `steps.length` either: calling to see a turn legitimately grows the hand, and that is not a new one.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setResults(model.steps.map(() => null)); }, [model.handId]);

  const commit = (key: RampAction['key'], amount?: string) => {
    let resolved = sv.grade(tag, key, amount);
    // factor the typed price into the score: an exact call + a %-within-3 keep the grade; a miss demotes it.
    if (hasPrice) {
      const callOk = Number(callTyped.replace(/[^0-9]/g, '')) === step.price.toCall;
      const pctOk = Math.abs(Number(pctTyped.replace(/[^0-9.]/g, '')) - reqEq) <= 3;
      if (!callOk || !pctOk) {
        resolved = {
          ...resolved,
          axisA: { ...resolved.axisA, cls: demote(resolved.axisA.cls), text: resolved.axisA.text + ' (price came in off).' },
          verdict: { ...resolved.verdict, cls: demote(resolved.verdict.cls) },
        } as ResolvedGrade;
      }
    }
    setResults((r) => { const next = r.slice(); next[i] = { grade: resolved, street: step.street }; return next; });
    if (key === 'fold') setFoldedStep(i); // TB4 - remember where he got out, so he can watch from the rail.
    loggedAtRef.current = i; // the decision the effect below is allowed to close the hand on.
    // A fold ends the hand and always has. Whether anything ELSE ends it is not knowable here - `isLast`
    // would be read off the model as it stood BEFORE this action, and that model's tail is the pro's
    // provisional line, not yours. On the demo seed's first hand the pro jams the 4-bet, so there are no
    // postflop steps and node 2 looks final - but CALL the 4-bet and you have just bought a flop, a turn and
    // a river. Solo used to report the hand over while hero was still in it. The effect below answers this
    // from the REBUILT model, which is the only thing that knows how the hand actually went.
    setPhase(key === 'fold' ? 'report' : 'logged');
    // FULL: play the decision for real, not just score it. See the note in LiveDiagnose.commit.
    onHeroDecision?.(i, key, amount);
    window.scrollTo({ top: 0 });
  };

  /**
   * FULL - your action may have ENDED the hand, and `isLast` above could not have known.
   *
   * `commit` decides 'logged' vs 'report' from the `isLast` of the model as it stood BEFORE your action was
   * played. Now that the hand really responds to you, that number moves: raise the flop and watch everyone
   * fold and the turn/river you were provisionally shown simply never happen. Without this, Solo would sit
   * in 'logged' offering a next step that no longer exists. Re-derived from the REBUILT model, which is the
   * only thing that knows how the hand actually ended.
   *
   * Guarded on `loggedAtRef` - the step hero actually committed at - and that guard is load-bearing. Without
   * it this effect also fires when he STEPS FORWARD, because `[i]` is in its deps and the reset effect above
   * shares that dep: advancing to the last step re-runs both, the reset sets 'play', and this one, reading
   * the 'logged' of the render it was queued from, sets 'report' afterwards and wins. Solo then skipped the
   * step hero had just navigated to and closed the hand early - turn→river on any four-step hand, and
   * (once a 3-bet that draws a 4-bet became playable) his second preflop decision. Only the decision he just
   * PLAYED may end the hand; the one he just walked up to may not.
   */
  useEffect(() => {
    if (phase === 'logged' && loggedAtRef.current === i && i >= model.steps.length - 1) setPhase('report');
  }, [model, i, phase]);

  // persist once the hand closes into the report.
  useEffect(() => {
    if (phase !== 'report' || persistedRef.current) return;
    persistedRef.current = true;
    void persistPlayedHand(
      results.map((e) => e ? { gradeClass: e.grade.verdict.cls, skillClasses: { value_extraction: e.grade.axisA.cls, player_profiling: e.grade.axisB.cls } } : null),
      'no_tips',
    ).then((res) => { if (!res.ok) console.warn('[progress] live solo persist failed:', res.error); });
  }, [phase, results]);

  // CX6 - settle this hand into the bankroll, but ONLY on a "sit with my roll" sit. A 'fresh' sit and the
  // Observe/Diagnose lenses never reach this line, which is the whole "they never touch the roll" guarantee.
  // App owns the actual accounting: it reads the authoritative net (`model.heroNetChips`) off its own table
  // and persists it, deduped + revisable by hand. This effect just says "the hand is done" - and re-says it
  // if the net changes (a revise from the report replays the hand into a different result), which is why it
  // is keyed on `model.heroNetChips` and not a fire-once ref.
  useEffect(() => {
    if (phase === 'report' && bankroll.choice === 'roll') bankroll.onSettleHand();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, model.heroNetChips, bankroll.choice]);

  /** TB2: advance the SESSION (same seats, carried stacks, rotated button); App's remount on the new hand
   *  number is what clears the per-hand state. */
  const nextHand = () => {
    persistedRef.current = false;
    setI(0);
    setFoldedStep(null);
    setObs(null);
    setReplaying(false);
    onNextHand();
  };

  const reportModel = useMemo(
    () => buildLiveReport(results.filter((e): e is LiveReportEntry => e !== null), 'solo'),
    [results],
  );

  /** TB4 - same lane as Diagnose, hung off Solo's report because Solo's fold commits straight into it (it
   *  shows no inline grade by design). The locked "no forced watching" fold path is unchanged: this is a
   *  button on the report, not a step in front of it. */
  const foldState = foldedStep !== null ? model.steps[foldedStep]?.state : undefined;
  const canWatch = foldState !== undefined && canWatchFold(model, foldState);
  const openWatch = () => {
    if (!foldState) return;
    const built = buildFoldedWalk(model, foldState);
    if (!built) return;
    const o = buildFoldedObservation(model, built, seatTags ?? new Map());
    setObs(o);
    if (o.evidence.length > 0) onConfirmReads?.(o.evidence);
    setPhase('watch');
    window.scrollTo({ top: 0 });
  };

  // CX6 - the sit-down GATE. Before a hand is played, Solo asks the one Joe-specified question: are you
  // playing for your roll, or sitting fresh? It short-circuits everything below (you have not chosen a hand
  // to play yet), and once chosen the very same session hand plays on. Reached only in Solo, so Observe and
  // Diagnose never show it.
  if (bankroll.choice === null) {
    const affordable = bankroll.roll.balance >= bankroll.buyIn;
    const net = bankroll.roll.balance - bankroll.roll.startingBalance;
    return (
      <div className="body">
        <div className="roll-gate">
          <div className="roll-gate-head">
            <div className="roll-gate-eyebrow">Solo &middot; {bankroll.stakesLabel}</div>
            <div className="roll-gate-h">Playing for your roll?</div>
            <div className="roll-gate-s">
              Solo is where the money is on the line. Sit with your bankroll and every hand moves it, or sit
              fresh to try new lines with nothing at stake.
            </div>
          </div>

          <button
            type="button"
            className={'roll-opt roll' + (affordable ? '' : ' off')}
            disabled={!affordable}
            onClick={() => bankroll.onChoose('roll')}
          >
            <div className="roll-opt-top">
              <span className="roll-opt-t">Sit with my roll</span>
              <span className="roll-opt-badge">{dollars(bankroll.roll.balance)}</span>
            </div>
            <div className="roll-opt-d">
              Buy in for {dollars(bankroll.buyIn)} from your bankroll. Wins and losses carry across sessions.
            </div>
            {bankroll.roll.handsPlayed > 0 && (
              <div className={'roll-opt-meta ' + (net >= 0 ? 'up' : 'down')}>
                {net >= 0 ? 'Up ' : 'Down '}
                {dollars(Math.abs(net))} over {bankroll.roll.handsPlayed} hand
                {bankroll.roll.handsPlayed === 1 ? '' : 's'}
              </div>
            )}
          </button>

          {!affordable && (
            <div className="roll-warn">
              Your roll will not cover a {dollars(bankroll.buyIn)} buy-in.{' '}
              <button type="button" className="roll-reset" onClick={bankroll.onResetRoll}>
                Reset roll to {dollars(bankroll.roll.startingBalance)}
              </button>
            </div>
          )}

          <button type="button" className="roll-opt fresh" onClick={() => bankroll.onChoose('fresh')}>
            <div className="roll-opt-top">
              <span className="roll-opt-t">Sit down fresh</span>
              <span className="roll-opt-badge ghost">No stakes</span>
            </div>
            <div className="roll-opt-d">
              No bankroll on the line. Play a while, experiment freely. Your roll stays untouched.
            </div>
          </button>

          <div className="roll-sim">
            Simulated play-money. A training score to focus the mind, never real money or gambling.
          </div>
        </div>
      </div>
    );
  }

  // CX6 - the running-roll readout, shown only on a bankroll sit. During play it names the tension; in the
  // report it shows how much this hand moved the roll (which App has already settled into the balance shown).
  const handNet = Math.round(model.heroNetChips);
  const rollStrip =
    bankroll.choice === 'roll' ? (
      <div className="roll-strip">
        <span className="roll-strip-l">
          Bankroll <b>{dollars(bankroll.roll.balance)}</b>
        </span>
        {phase === 'report' ? (
          <span className={'roll-strip-net ' + (handNet >= 0 ? 'up' : 'down')}>
            {handNet >= 0 ? '+' : '-'}
            {dollars(Math.abs(handNet))} this hand
          </span>
        ) : (
          <span className="roll-strip-live">Money on the line</span>
        )}
      </div>
    ) : null;

  if (phase === 'watch' && obs) {
    return (
      <FoldedObservationView
        obs={obs}
        onBack={() => { setPhase('report'); window.scrollTo({ top: 0 }); }}
        onReport={() => { setPhase('report'); window.scrollTo({ top: 0 }); }}
      />
    );
  }

  // the debrief replay - the whole hand, watched back with the pro's read (the SAME cinematic renderer, watch
  // mode, observe narration). This is where Solo's narration lives: silent while you play, spoken in review.
  if (phase === 'report' && replaying) {
    return (
      <>
        <CinematicFelt key={`replay:${model.handId}`} stream={stream} beats={debriefBeats} controls />
        <div className="body">
          <div className="obsresult">
            <span className="ort">The hand, with the read</span>
            <span className="orv">Scrub back and forth. The pro talks you through it.</span>
          </div>
          {debriefProse.length > 0 && (
            <div className="libprose">
              <div className="libprose-h">The pro's read, street by street</div>
              {debriefProse.map((p, i) => (
                <div className="libprose-beat" key={i}>
                  <span className="libprose-street">{p.street}</span>
                  {p.text.what_changed ? <p className="libprose-wc">{p.text.what_changed}</p> : null}
                  <p className="libprose-pro">{p.text.pro_thought}</p>
                  <p className="libprose-villain">{p.text.villain_read}</p>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="dock">
          <div className="dockrow">
            <button type="button" className="cta" onClick={() => { setReplaying(false); window.scrollTo({ top: 0 }); }}>
              Back to the report
            </button>
          </div>
        </div>
      </>
    );
  }

  if (phase === 'report') {
    return (
      <>
        <div className="body">
          {rollStrip}
          <EndOfHandReport model={reportModel} />
        </div>
        <div className="dock">
          <div className="dockcol">
            <button type="button" className="cta ghost cf-open" onClick={() => { setReplaying(true); window.scrollTo({ top: 0 }); }}>
              Watch the hand back, with the read
            </button>
            {canWatch && (
              <button type="button" className="cta ghost cf-open" onClick={openWatch}>
                Watch the table without you
              </button>
            )}
            <div className="dockrow">
              <button type="button" className="cta ghost prev" onClick={() => setPhase('play')}>
                Back
              </button>
              <button type="button" className="cta" onClick={nextHand}>
                Play another hand
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  // CX4 - the cinematic felt halts at hero's decision (phase 'play') or one frame past it to show the action
  // he just logged (phase 'logged'). SILENT live (no beats) - Solo's narration is the debrief replay above.
  // `arrived` arms the ramp only once the action has reached hero.
  const decisionFrame = heroDecisionFrame(step);
  const haltAt = phase === 'play' ? decisionFrame : heroActionFrame(step, stream);
  const arrived = feltIndex >= decisionFrame;

  return (
    <>
      <CinematicFelt
        key={model.handId}
        stream={stream}
        haltAt={haltAt}
        autoplay
        controls={false}
        onIndex={setFeltIndex}
      />

      <div className="body">
        {rollStrip}
        <div>
          <div className="kicker">{sv.headline.kicker}</div>
          <div className="h2">{sv.headline.h2}</div>
          <div className="lede">{sv.headline.lede}</div>
        </div>

        {pfctx && phase === 'play' && (
          <div className="pfctx">
            <span className="pfctx-k">How the pot got here</span>
            <span className="pfctx-t">{pfctx}</span>
          </div>
        )}

        <StreetStepper street={sv.streetIndex} onSelect={() => { /* stepped by committing */ }} />

        {phase === 'play' ? (
          <>
            <ReadLockIn
              tagSub={sv.headline.tagSub}
              families={sv.families}
              tag={tag}
              weights={weights}
              locked={locked}
              onTag={setTag}
              onCycleWeight={(fi) => setWeights((w) => w.map((v, j) => (j === fi ? (v + 1) % 4 : v)))}
              onLock={() => {
                setLock((l) => lockReducer(l, { type: 'lockIn', atDecisionIndex: i }));
                // TB3 - persist the read on the seat. TB5 - persist to THIS node's villain (`step.villainSeat`),
                // the man the question named and Axis B graded, not the hand-level `model.villainSeat`.
                if (tag) onLockRead?.(step.villainSeat, tag);
              }}
              onRevise={() => setLock((l) => lockReducer(l, { type: 'edit' }))}
            />
            {locked && hasPrice && (
              <SoloPriceEntry
                pot={money(step.price.pot)}
                hisBet={money(step.price.toCall)}
                call={callTyped}
                pct={pctTyped}
                onCall={setCallTyped}
                onPct={setPctTyped}
              />
            )}
          </>
        ) : (
          <div className="solog">
            <div className="t">{step.kind === 'preflop' ? 'Decision logged' : 'Street logged'}</div>
            <div className="s">Your read, your price, and your play are in. You will see how they scored at the end of the hand.</div>
            <div className="pend">
              {model.steps.map((_, k) => (
                <i key={k} className={results[k] ? 'done' : ''} />
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="dock">
        {phase === 'play' ? (
          <LiveActionRamp
            actions={sv.ramp}
            bet={sv.betDef}
            locked={locked}
            priceReady={priceReady && arrived}
            hint={!locked ? 'Lock your read first.' : !priceReady ? 'Now work out the price, then choose your action.' : !arrived ? 'The action is coming to you…' : 'Read and price in. Choose your action.'}
            onCommit={commit}
          />
        ) : (
          <div className="dockrow">
            <button type="button" className="cta ghost prev" onClick={() => setPhase('play')}>
              Revise
            </button>
            <button type="button" className="cta" onClick={() => setI(i + 1)}>
              {/* FULL: preflop→preflop (he 3-bet, a villain re-raised) is not a new street. */}
              {model.steps[i + 1]?.kind === 'preflop' ? 'Next decision' : 'Next street'}
            </button>
          </div>
        )}
      </div>
    </>
  );
}
